import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FaRocket,
  FaShieldAlt,
  FaChartLine,
  FaWallet,
  FaUsers,
  FaCheckCircle,
  FaArrowRight,
  FaStar,
  FaBolt,
  FaGem,
  FaChevronDown,
  FaLayerGroup,
  FaLock,
} from "react-icons/fa";

const Landing = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Auto-rotate features
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const features = [
    {
      title: "Stage Rewards",
      desc: "26 progressive stages with up to $10,000 in total earnings",
      icon: FaLayerGroup,
      gradient: "from-amber-500 to-orange-500",
      glow: "shadow-amber-500/30",
      stat: "$10K",
      statLabel: "Total Rewards",
    },
    {
      title: "Referral System",
      desc: "3-level deep commissions — 10%, 3%, and 2% on every activation",
      icon: FaUsers,
      gradient: "from-emerald-500 to-teal-500",
      glow: "shadow-emerald-500/30",
      stat: "15%",
      statLabel: "Commission Pool",
    },
    {
      title: "Secure Wallet",
      desc: "BEP20 USDT deposits, real-time tracking, and instant withdrawals",
      icon: FaShieldAlt,
      gradient: "from-violet-500 to-purple-500",
      glow: "shadow-violet-500/30",
      stat: "24/7",
      statLabel: "Always Active",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-hidden">
      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[60vw] h-[60vw] rounded-full bg-cyan-500/[0.07] blur-[120px]" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[50vw] h-[50vw] rounded-full bg-violet-500/[0.07] blur-[120px]" />
        <div className="absolute top-[40%] left-[50%] w-[30vw] h-[30vw] rounded-full bg-amber-500/[0.04] blur-[100px]" />
      </div>

      {/* Sticky Nav */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ?
            "bg-slate-950/80 backdrop-blur-xl border-b border-white/[0.06]"
          : ""
        }`}
      >
        <nav className="max-w-7xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 to-violet-500 flex items-center justify-center shadow-lg shadow-cyan-500/30">
              <span className="text-white font-extrabold text-sm">L</span>
            </div>
            <div>
              <div className="text-sm font-bold tracking-wider">LOOPS</div>
              <div className="text-[9px] text-cyan-300/70 font-medium tracking-wide">
                DIGITAL FUND
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/login"
              className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-300 border border-white/10 active:scale-[0.97] transition-transform touch-manipulation"
            >
              Sign In
            </Link>
            <Link
              to="/register"
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-cyan-500 to-violet-500 text-xs font-semibold text-white shadow-lg shadow-cyan-500/20 active:scale-[0.97] transition-transform touch-manipulation"
            >
              Start
            </Link>
          </div>
        </nav>
      </header>

      {/* ==================== HERO ==================== */}
      <section className="relative z-10 pt-24 pb-8 px-5">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left content */}
            <div>
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/20 bg-cyan-500/[0.08]">
                <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                <span className="text-[10px] text-cyan-300 font-semibold tracking-wider uppercase">
                  Next-Gen Fund Platform
                </span>
              </div>

              <h1 className="mt-5 text-[2.5rem] leading-[1.1] lg:text-6xl font-extrabold tracking-tight">
                <span className="text-white">Grow your</span>
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-sky-400 to-violet-400">
                  community wealth
                </span>
                <br />
                <span className="text-white">together.</span>
              </h1>

              <p className="mt-5 text-[15px] leading-relaxed text-slate-400 max-w-md">
                LOOPS is a transparent donation system with 26 growth stages,
                3-level referral commissions, and automated crypto payouts.
              </p>

              {/* CTA Buttons */}
              <div className="mt-7 flex items-center gap-3">
                <Link
                  to="/register"
                  className="flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-gradient-to-r from-cyan-500 to-violet-500 text-sm font-bold text-white shadow-xl shadow-cyan-500/20 active:scale-[0.97] transition-transform touch-manipulation"
                >
                  Get Started
                  <FaArrowRight className="text-xs" />
                </Link>
                <Link
                  to="/login"
                  className="flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-white/[0.06] border border-white/10 text-sm font-semibold text-slate-300 active:scale-[0.97] transition-transform touch-manipulation"
                >
                  Dashboard
                </Link>
              </div>

              {/* Trust badges */}
              <div className="mt-7 flex items-center gap-4">
                {[
                  { icon: FaLock, text: "Secure" },
                  { icon: FaBolt, text: "Instant" },
                  { icon: FaCheckCircle, text: "Transparent" },
                ].map((badge) => {
                  const Icon = badge.icon;
                  return (
                    <div key={badge.text} className="flex items-center gap-1.5">
                      <Icon className="text-[10px] text-cyan-400" />
                      <span className="text-[11px] text-slate-500 font-medium">
                        {badge.text}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right - Stats showcase (visible on desktop, scrolled to on mobile) */}
            <div className="hidden lg:block relative">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-violet-500/10 rounded-3xl blur-2xl" />
              <div className="relative rounded-2xl bg-white/[0.03] border border-white/[0.06] p-6 space-y-4">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-violet-500 flex items-center justify-center">
                    <FaChartLine className="text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-white">
                      Network Pulse
                    </p>
                    <p className="text-[11px] text-slate-500">
                      Live ecosystem metrics
                    </p>
                  </div>
                </div>
                {[
                  {
                    label: "Stage Progress",
                    pct: 72,
                    color: "from-amber-400 to-orange-500",
                  },
                  {
                    label: "Referral Growth",
                    pct: 81,
                    color: "from-emerald-400 to-teal-500",
                  },
                  {
                    label: "Payout Velocity",
                    pct: 90,
                    color: "from-cyan-400 to-violet-500",
                  },
                ].map((metric) => (
                  <div key={metric.label} className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-400">
                        {metric.label}
                      </span>
                      <span className="text-xs font-bold text-white">
                        {metric.pct}%
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                      <div
                        className={`h-full rounded-full bg-gradient-to-r ${metric.color}`}
                        style={{ width: `${metric.pct}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Scroll hint on mobile */}
        <div className="flex justify-center mt-8 lg:hidden animate-bounce">
          <FaChevronDown className="text-slate-600 text-sm" />
        </div>
      </section>

      {/* ==================== KEY NUMBERS ==================== */}
      <section className="relative z-10 px-5 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-3 gap-3">
            {[
              {
                value: "26",
                label: "Stages",
                gradient: "from-amber-500/15 to-orange-500/15",
                border: "border-amber-500/20",
                color: "text-amber-400",
              },
              {
                value: "$10K",
                label: "Rewards",
                gradient: "from-emerald-500/15 to-teal-500/15",
                border: "border-emerald-500/20",
                color: "text-emerald-400",
              },
              {
                value: "10%",
                label: "Direct",
                gradient: "from-cyan-500/15 to-violet-500/15",
                border: "border-cyan-500/20",
                color: "text-cyan-400",
              },
            ].map((stat) => (
              <div
                key={stat.label}
                className={`py-4 px-3 rounded-2xl bg-gradient-to-br ${stat.gradient} border ${stat.border} text-center`}
              >
                <p className={`text-2xl font-extrabold ${stat.color}`}>
                  {stat.value}
                </p>
                <p className="text-[10px] text-slate-500 font-medium mt-0.5">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== FEATURES CAROUSEL ==================== */}
      <section className="relative z-10 px-5 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-[10px] text-cyan-400 font-semibold tracking-widest uppercase">
              Why LOOPS
            </p>
            <h2 className="mt-2 text-2xl lg:text-4xl font-extrabold tracking-tight">
              Built for{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-violet-400">
                transparency
              </span>
            </h2>
          </div>

          {/* Feature cards - interactive on mobile */}
          <div className="space-y-3">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              const isActive = activeFeature === idx;
              return (
                <button
                  key={feature.title}
                  onClick={() => setActiveFeature(idx)}
                  className={`w-full text-left transition-all duration-300 rounded-2xl border overflow-hidden active:scale-[0.99] touch-manipulation ${
                    isActive ?
                      `bg-gradient-to-r ${feature.gradient} border-white/20 shadow-xl ${feature.glow}`
                    : "bg-white/[0.03] border-white/[0.06]"
                  }`}
                >
                  <div className="p-4">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          isActive ? "bg-white/20" : "bg-white/[0.06]"
                        }`}
                      >
                        <Icon
                          className={`text-sm ${isActive ? "text-white" : "text-slate-400"}`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-sm font-bold ${isActive ? "text-white" : "text-white"}`}
                        >
                          {feature.title}
                        </p>
                        <p
                          className={`text-[11px] mt-0.5 leading-relaxed ${isActive ? "text-white/80" : "text-slate-500"}`}
                        >
                          {feature.desc}
                        </p>
                      </div>
                      <div
                        className={`flex-shrink-0 text-right ${isActive ? "" : "opacity-50"}`}
                      >
                        <p
                          className={`text-xl font-extrabold ${isActive ? "text-white" : "text-slate-500"}`}
                        >
                          {feature.stat}
                        </p>
                        <p
                          className={`text-[9px] font-medium ${isActive ? "text-white/60" : "text-slate-600"}`}
                        >
                          {feature.statLabel}
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* Progress indicator */}
                  {isActive && (
                    <div className="h-0.5 bg-white/20">
                      <div
                        className="h-full bg-white/60 animate-[grow_4s_linear]"
                        style={{ animation: "grow 4s linear forwards" }}
                      />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-1.5 mt-4">
            {features.map((_, idx) => (
              <div
                key={idx}
                className={`h-1 rounded-full transition-all duration-300 ${
                  activeFeature === idx ? "w-6 bg-cyan-400" : "w-1 bg-white/20"
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ==================== HOW IT WORKS ==================== */}
      <section className="relative z-10 px-5 py-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-[10px] text-cyan-400 font-semibold tracking-widest uppercase">
              Get Started
            </p>
            <h2 className="mt-2 text-2xl lg:text-4xl font-extrabold tracking-tight">
              Three simple steps
            </h2>
          </div>

          {/* Step timeline */}
          <div className="space-y-0">
            {[
              {
                step: "01",
                title: "Register",
                desc: "Create your account and get a unique referral code to share",
                icon: FaRocket,
                gradient: "from-cyan-500 to-sky-500",
              },
              {
                step: "02",
                title: "Activate",
                desc: "Send $50 USDT to activate your position and unlock all features",
                icon: FaWallet,
                gradient: "from-violet-500 to-purple-500",
              },
              {
                step: "03",
                title: "Earn",
                desc: "Build your network, claim stage bonuses, and withdraw anytime",
                icon: FaGem,
                gradient: "from-amber-500 to-orange-500",
              },
            ].map((step, idx) => {
              const Icon = step.icon;
              const isLast = idx === 2;
              return (
                <div key={step.step} className="flex gap-4">
                  {/* Timeline spine */}
                  <div className="flex flex-col items-center flex-shrink-0 w-8">
                    <div
                      className={`w-8 h-8 rounded-xl bg-gradient-to-br ${step.gradient} flex items-center justify-center shadow-lg z-10`}
                    >
                      <Icon className="text-xs text-white" />
                    </div>
                    {!isLast && (
                      <div className="w-0.5 flex-1 min-h-[24px] bg-white/[0.06]" />
                    )}
                  </div>
                  {/* Content */}
                  <div className={`flex-1 ${isLast ? "pb-0" : "pb-6"}`}>
                    <div className="px-4 py-3.5 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] text-cyan-400 font-bold">
                          {step.step}
                        </span>
                        <span className="text-sm font-bold text-white">
                          {step.title}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 leading-relaxed">
                        {step.desc}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ==================== FULL FEATURES GRID ==================== */}
      <section className="relative z-10 px-5 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              {
                title: "Secure Deposits",
                icon: FaShieldAlt,
                desc: "Real-time BEP20 tracking",
                color: "text-emerald-400",
                bg: "bg-emerald-500/10",
              },
              {
                title: "Auto Payouts",
                icon: FaBolt,
                desc: "Stage rewards on claim",
                color: "text-amber-400",
                bg: "bg-amber-500/10",
              },
              {
                title: "3-Level Refs",
                icon: FaUsers,
                desc: "10% + 3% + 2% commissions",
                color: "text-cyan-400",
                bg: "bg-cyan-500/10",
              },
              {
                title: "Live Wallet",
                icon: FaWallet,
                desc: "Balance & history in one place",
                color: "text-violet-400",
                bg: "bg-violet-500/10",
              },
              {
                title: "26 Stages",
                icon: FaLayerGroup,
                desc: "Progressive milestones",
                color: "text-rose-400",
                bg: "bg-rose-500/10",
              },
              {
                title: "Withdrawals",
                icon: FaStar,
                desc: "PIN-secured, anytime",
                color: "text-sky-400",
                bg: "bg-sky-500/10",
              },
            ].map((feature) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.title}
                  className="p-4 rounded-2xl bg-white/[0.03] border border-white/[0.06]"
                >
                  <div
                    className={`w-9 h-9 rounded-xl ${feature.bg} flex items-center justify-center mb-3`}
                  >
                    <Icon className={`text-sm ${feature.color}`} />
                  </div>
                  <p className="text-sm font-bold text-white">
                    {feature.title}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ==================== FINAL CTA ==================== */}
      <section className="relative z-10 px-5 py-10">
        <div className="max-w-7xl mx-auto">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-cyan-500 via-sky-600 to-violet-600 p-8 lg:p-12 text-center shadow-2xl shadow-cyan-500/10">
            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-white/10 blur-3xl" />
            <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-white/10 blur-3xl" />
            <div className="relative z-10">
              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-5 border border-white/20">
                <FaRocket className="text-xl text-white" />
              </div>
              <h3 className="text-2xl lg:text-4xl font-extrabold text-white leading-tight">
                Ready to start
                <br />
                your loop?
              </h3>
              <p className="mt-3 text-sm text-white/70 max-w-md mx-auto leading-relaxed">
                Join the next-gen donation platform built for transparent
                community growth.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/register"
                  className="flex items-center justify-center gap-2 px-7 py-3.5 rounded-2xl bg-white text-slate-900 text-sm font-bold shadow-xl active:scale-[0.97] transition-transform touch-manipulation"
                >
                  Create Account
                  <FaArrowRight className="text-xs" />
                </Link>
                <Link
                  to="/login"
                  className="flex items-center justify-center gap-2 px-7 py-3.5 rounded-2xl bg-white/15 border border-white/20 text-white text-sm font-semibold active:scale-[0.97] transition-transform touch-manipulation"
                >
                  Sign In
                </Link>
              </div>
              <div className="mt-6 flex items-center justify-center gap-4">
                {["Secure", "Transparent", "Automated"].map((text) => (
                  <div key={text} className="flex items-center gap-1.5">
                    <FaCheckCircle className="text-[10px] text-white/60" />
                    <span className="text-[11px] text-white/60 font-medium">
                      {text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 py-8 border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-5 flex flex-col items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-cyan-400 to-violet-500 flex items-center justify-center">
              <span className="text-white font-extrabold text-[9px]">L</span>
            </div>
            <span className="text-xs font-bold tracking-wider text-slate-500">
              LOOPS
            </span>
          </div>
          <p className="text-[11px] text-slate-600">
            &copy; 2026 LOOPS — Digital Community Fund
          </p>
        </div>
      </footer>

      {/* CSS animation for feature progress bar */}
      <style>{`
        @keyframes grow {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default Landing;
